/**
 * User Context
 * 
 * Manages the current user session and provides user data throughout the app.
 * 
 * Flow with Azure Static Web Apps authentication:
 * 1. Check SWA authentication status via /.auth/me
 * 2. If not authenticated, redirect to /.auth/login/aad
 * 3. Once authenticated, fetch user list from API and match the logged-in user
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { User } from '../data/types';
import { getUsers } from '../services/users';
import { setCurrentUserId } from '../services/api';
import { clearAllConversations } from '../lib/assistantStorage';

// ============================================================================
// TYPES
// ============================================================================

interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims?: Array<{ typ: string; val: string }>;
}

interface SwaAuthResponse {
  clientPrincipal: SwaClientPrincipal | null;
}

interface UserContextValue {
  currentUser: User | null;
  availableUsers: User[];
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  swaUser: SwaClientPrincipal | null;
  // Refresh the users list
  refreshUsers: () => Promise<void>;
  // Auth actions
  login: () => void;
  logout: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const UserContext = createContext<UserContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [swaUser, setSwaUser] = useState<SwaClientPrincipal | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check SWA authentication status
  const checkSwaAuth = useCallback(async (): Promise<SwaClientPrincipal | null> => {
    try {
      const response = await fetch('/.auth/me');
      if (!response.ok) {
        return null;
      }
      const data: SwaAuthResponse = await response.json();
      return data.clientPrincipal;
    } catch (err) {
      console.error('Failed to check SWA auth:', err);
      return null;
    }
  }, []);

  // Fetch users from the API
  const fetchUsers = useCallback(async (swaClientPrincipal: SwaClientPrincipal) => {
    try {
      const result = await getUsers();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch users');
      }

      setAvailableUsers(result.data);
      
      // Try to find the user matching the SWA login email
      const matchedUser = result.data.find(u => 
        u.email.toLowerCase() === swaClientPrincipal.userDetails.toLowerCase()
      );
      
      if (matchedUser) {
        setCurrentUser(matchedUser);
        setCurrentUserId(matchedUser.userId);
      } else {
        // User authenticated with Azure AD but doesn't exist in Internal Users
        // This is an unauthorized access attempt - do NOT fall back to another user
        setError(`Access denied. Your email (${swaClientPrincipal.userDetails}) is not registered as an internal user. Please contact an administrator.`);
        console.error(`Unauthorized access attempt: ${swaClientPrincipal.userDetails} is not in the internal users list`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(message);
      console.error('UserContext error:', message);
    }
  }, []);

  // Initialize on mount - check auth first
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Check SWA authentication
        const clientPrincipal = await checkSwaAuth();

        if (!clientPrincipal) {
          // Not authenticated — kick off the redirect to login. Crucially
          // we do NOT drop isLoading here: the browser navigation is async,
          // so if we re-render with isLoading=false + swaUser=null + no error,
          // App.tsx falls through to <Routes> and the dashboard renders for
          // one frame before the page unloads (the "dashboard flash" symptom).
          // Staying in the loading state until navigation completes hides it.
          window.location.href =
            '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
          return;
        }

        setSwaUser(clientPrincipal);

        // Step 2: Fetch users from API
        await fetchUsers(clientPrincipal);
        setIsLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to initialize';
        setError(message);
        console.error('UserContext initialization error:', message);
        // On error we DO drop isLoading so the AccessDenied UI can render
        // instead of leaving the user staring at the loading spinner forever.
        setIsLoading(false);
      }
    };

    initialize();
  }, [checkSwaAuth, fetchUsers]);

  // Refresh users list
  const refreshUsers = useCallback(async () => {
    if (swaUser) {
      await fetchUsers(swaUser);
    }
  }, [fetchUsers, swaUser]);

  const login = () => {
    // Logout first to clear any stale SWA session cookie before re-authenticating
    window.location.href = '/.auth/logout?post_logout_redirect_uri=' +
      encodeURIComponent('/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname));
  };

  const logout = async () => {
    try {
      await clearAllConversations();
    } catch {
      // Best-effort — don't block logout if IndexedDB fails
    }
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  };

  const value: UserContextValue = {
    currentUser,
    availableUsers,
    isLoading,
    error,
    isAuthenticated: !!swaUser && !!currentUser,
    swaUser,
    refreshUsers,
    login,
    logout,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  
  return context;
}

/**
 * Hook to get just the current user, throwing if not authenticated
 * Use this when you need to guarantee the user is logged in
 */
export function useCurrentUser(): User {
  const { currentUser, isAuthenticated } = useUser();
  
  if (!isAuthenticated || !currentUser) {
    throw new Error('User is not authenticated');
  }
  
  return currentUser;
}
